import axios from 'axios';

import axiosInstance from 'src/axiosInstance';
import { AppDispatch } from 'src/state/store';
import { setShowAlert } from 'src/state/utilts/utilsSlice';
import { TOAST_TYPE } from 'src/utils/constantTypes';
import { GetReportsByFilterV2PayloadType } from 'src/state/reportV2/interfaces';
import { AccessToken } from 'src/state/user/interfaces';
import {
  BulkAfterCareSummaryReportItem,
  GenerateSummaryAndSpecialistPayload,
  ReportContentPayload,
  SendTcNotesEmailPayload,
  SendTmfWellnessReportPayload,
  UpdateSummaryAndSpecialistPayload,
  UpdateTmfWellnessReportPayload
} from './types';

type PaginationAndSortParams = {
  offset: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
};

export const getReportsByFiltersV2 = async (
  payload: GetReportsByFilterV2PayloadType,
  pagingAndSort: PaginationAndSortParams,
  { dispatch, signal }: { dispatch: AppDispatch; signal?: AbortSignal }
) => {
  try {
    const res = await axiosInstance.get('report/all', {
      params: {
        ...payload?.filters,
        offset: pagingAndSort.offset,
        limit: pagingAndSort.limit,
        ...(pagingAndSort.sortBy ? { sortBy: pagingAndSort.sortBy } : {}),
        ...(pagingAndSort.sortOrder
          ? { sortOrder: pagingAndSort.sortOrder }
          : {})
      },
      headers: {
        Authorization: `Bearer ${payload?.accessToken}`
      },
      signal
    });
    return res?.data;
  } catch (err) {
    if (axios.isCancel(err)) {
      throw err instanceof Error
        ? err
        : new Error('Report fetch request was cancelled.');
    }
    if (err?.response?.data?.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message:
            err?.response?.data?.msg ||
            'An error occurred while fetching reports.',
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while fetching reports.'
    );
  }
};

export const exportReportsFile = async (
  payload: GetReportsByFilterV2PayloadType,
  sort: { sortBy?: string; sortOrder?: 'ASC' | 'DESC' },
  { dispatch }: { dispatch: AppDispatch }
): Promise<Blob> => {
  try {
    const res = await axiosInstance.get('report/export', {
      params: {
        ...payload?.filters,
        ...(sort?.sortBy ? { sortBy: sort.sortBy } : {}),
        ...(sort?.sortOrder ? { sortOrder: sort.sortOrder } : {})
      },
      headers: {
        Authorization: `Bearer ${payload?.accessToken}`
      },
      responseType: 'arraybuffer'
    });
    return new Blob([res.data], { type: 'text/csv;charset=utf-8' });
  } catch (err) {
    // Error body is an ArrayBuffer when responseType is 'arraybuffer'.
    if (err?.response?.status !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message: 'An error occurred while exporting reports.',
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error('An error occurred while exporting reports.');
  }
};

export const getReportContentByType = async ({
  accessToken,
  data
}: AccessToken & { data: ReportContentPayload }) => {
  try {
    const res = await axiosInstance.post('report/content', data, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while fetching report content.'
    );
  }
};

export const sendBulkAfterCareSummaryReports = async ({
  accessToken,
  data
}: AccessToken & { data: BulkAfterCareSummaryReportItem[] }) => {
  try {
    const res = await axiosInstance.post(
      'report/send-after-care-summary-report',
      { data },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while sending bulk after care summary reports.'
    );
  }
};

export const sendTcNotesEmail = async ({
  accessToken,
  data
}: AccessToken & { data: SendTcNotesEmailPayload }) => {
  try {
    const res = await axiosInstance.post('report/send-tc-notes-email', data, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while sending TC notes email.'
    );
  }
};

export const sendTmfWellnessReportEmail = async ({
  accessToken,
  data
}: AccessToken & { data: SendTmfWellnessReportPayload }) => {
  try {
    const res = await axiosInstance.post(
      'report/send-tmf-wellness-report',
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while sending Oral Health & Wellness Report email.'
    );
  }
};

export const updateSummaryAndSpecialistReport = async ({
  accessToken,
  data
}: AccessToken & { data: UpdateSummaryAndSpecialistPayload }) => {
  try {
    const res = await axiosInstance.post(
      'report/updateSummaryAndSpecialistReport',
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while updating report.'
    );
  }
};

export const generateSummaryAndSpecialistReport = async ({
  accessToken,
  data
}: AccessToken & { data: GenerateSummaryAndSpecialistPayload }) => {
  try {
    const res = await axiosInstance.post('report/generate-report', data, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while generating report.'
    );
  }
};

export const getTmfWellnessReport = async ({
  accessToken,
  requestId
}: AccessToken & { requestId: string }) => {
  try {
    const res = await axiosInstance.get('report/get-tmf-report', {
      params: { requestId },
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        err?.response?.data?.message ||
        'An error occurred while fetching Oral Health & Wellness Report.'
    );
  }
};

export const generateTmfWellnessReport = async ({
  accessToken,
  requestId
}: AccessToken & { requestId: string }) => {
  try {
    const res = await axiosInstance.post(
      'report/generate-tmf-report',
      { requestId },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        err?.response?.data?.message ||
        'An error occurred while generating Oral Health & Wellness Report.'
    );
  }
};

export const updateTmfWellnessReport = async ({
  accessToken,
  data
}: AccessToken & { data: UpdateTmfWellnessReportPayload }) => {
  try {
    const res = await axiosInstance.post('report/update-tmf-report', data, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return res?.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        err?.response?.data?.message ||
        'An error occurred while updating Oral Health & Wellness Report.'
    );
  }
};
